import type { OpeningHours } from "../../../mocks/venueDetails";

interface VenueInfoBarProps {
  address: string;
  phone: string;
  website: string;
  email: string;
  lat: number;
  lng: number;
  priceRange: string;
  openingHours: OpeningHours[];
  tags: string[];
}

const TODAY_IDX = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;

export default function VenueInfoBar({
  address, phone, website, email, lat, lng, priceRange, openingHours, tags,
}: VenueInfoBarProps) {
  const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left: Contact + Tags */}
      <div className="space-y-5">
        {/* Contact */}
        <div className="bg-stone-50 rounded-2xl p-5 space-y-3">
          <h3 className="font-semibold text-stone-800 text-sm uppercase tracking-wider">İletişim</h3>

          <a href={mapsUrl} target="_blank" rel="nofollow noreferrer"
            className="flex items-start gap-3 text-stone-600 hover:text-stone-800 transition-colors cursor-pointer group"
          >
            <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-map-pin-line text-lg text-emerald-600" />
            </span>
            <span className="text-sm leading-relaxed group-hover:underline">{address}</span>
          </a>

          {phone && (
            <a href={`tel:${phone}`}
              className="flex items-center gap-3 text-stone-600 hover:text-stone-800 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-line text-lg text-emerald-600" />
              </span>
              <span className="text-sm">{phone}</span>
            </a>
          )}

          {website && (
            <a href={`https://${website}`} target="_blank" rel="nofollow noreferrer"
              className="flex items-center gap-3 text-stone-600 hover:text-stone-800 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-global-line text-lg text-emerald-600" />
              </span>
              <span className="text-sm truncate">{website}</span>
            </a>
          )}

          {email && (
            <a href={`mailto:${email}`}
              className="flex items-center gap-3 text-stone-600 hover:text-stone-800 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line text-lg text-emerald-600" />
              </span>
              <span className="text-sm truncate">{email}</span>
            </a>
          )}

          {priceRange && (
            <div className="flex items-center gap-3 text-stone-600">
              <span className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-price-tag-3-line text-lg text-emerald-600" />
              </span>
              <span className="text-sm font-medium">{priceRange}</span>
            </div>
          )}
        </div>

        {/* Tags */}
        <div className="bg-stone-50 rounded-2xl p-5">
          <h3 className="font-semibold text-stone-800 text-sm uppercase tracking-wider mb-3">Etiketler</h3>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span key={tag} className="px-3 py-1 bg-white border border-stone-200 rounded-full text-xs text-stone-600 font-medium">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Directions button */}
        <a
          href={directionsUrl}
          target="_blank"
          rel="nofollow noreferrer"
          className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl py-3 px-4 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-navigation-line text-base" />
          Konum Tarifi Al
        </a>
      </div>

      {/* Right: Opening Hours */}
      <div>
        <div className="bg-stone-50 rounded-2xl p-5 h-full">
          <h3 className="font-semibold text-stone-800 text-sm uppercase tracking-wider mb-4">Çalışma Saatleri</h3>
          <div className="space-y-2">
            {openingHours.map((item, idx) => (
              <div
                key={item.day}
                className={`flex justify-between items-center py-2 px-3 rounded-lg text-sm transition-colors ${idx === TODAY_IDX ? "bg-emerald-700 text-white" : "text-stone-600"}`}
              >
                <span className={`font-medium ${idx === TODAY_IDX ? "text-white" : "text-stone-700"}`}>
                  {item.day}
                  {idx === TODAY_IDX && <span className="ml-2 text-xs opacity-80">(Bugün)</span>}
                </span>
                <span className={`${!item.isOpen ? "text-red-400" : idx === TODAY_IDX ? "text-white" : "text-stone-500"}`}>
                  {item.hours}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
